import {
  CornerInfoWithProductInfo,
  DesignProductMatchingSummary,
  DrawingShapeItem,
  SelectedItemWithFullRecord,
} from '~/core/hooks/useEquivalentProductMatching/interfaces/ProductMatchingSummary';
import { DrawingAppliance, EdgeModel } from '~/core/models';
import { getProductDescription, isSomething } from '~/core/utils';

export type RemovedProduct = {
  id: string;
  imageUrl: string;
  name: string;
  type:
    | 'edge'
    | 'appliance'
    | 'corner'
    | 'cutout'
    | 'tearOut'
    | 'chipMinimizer'
    | 'plumbing'
    | 'selectedOptionalProducts';
};

const isBaseModel = (item: DrawingShapeItem): item is EdgeModel =>
  'productId' in item && 'name' in item && 'imageUrl' in item;

const isDrawingAppliance = (item: DrawingShapeItem): item is DrawingAppliance =>
  'applianceModel' in item &&
  item.applianceModel !== undefined &&
  'centerPoint' in item;

const isCornerInfo = (
  item: DrawingShapeItem
): item is CornerInfoWithProductInfo =>
  'cornerInfo' in item && 'productName' in item && 'imageUrl' in item;

const mapDrawingShapeItemToRemovedProduct = (
  item: DrawingShapeItem
): RemovedProduct | undefined => {
  if (isDrawingAppliance(item)) {
    return {
      id: String(item.applianceModel.productId),
      imageUrl:
        item.applianceModel.beautyShotImageUrl ||
        item.applianceModel.imageUrl ||
        '',
      name: getProductDescription(item.applianceModel),
      type: 'appliance',
    };
  }

  if (isCornerInfo(item)) {
    return {
      id: String(item.cornerInfo.productId),
      imageUrl: item.imageUrl,
      name: item.productName,
      type: 'corner',
    };
  }

  // This must be an Edge case
  if (isBaseModel(item)) {
    return {
      id: String(item.productId),
      imageUrl: item.imageUrl,
      name: getProductDescription(item),
      type: 'edge',
    };
  }

  return undefined;
};

/**
 * Collects all removed products from the design product matching summary
 * and formats them for display in the ProductChangesRequiredModal.
 *
 * @param summary - The design product matching summary containing removed products
 * @returns An array of removed products formatted for modal display
 */
export const collectRemovedProducts = (
  summary: DesignProductMatchingSummary
): RemovedProduct[] => {
  const removedProductsFromShape: RemovedProduct[] = (
    summary.drawingShapes.removed ?? []
  )
    .map(mapDrawingShapeItemToRemovedProduct)
    .filter(isSomething);

  const otherProducts: [
    SelectedItemWithFullRecord[],
    RemovedProduct['type'],
  ][] = [
    [summary.tearOuts?.removed ?? [], 'tearOut'],
    [summary.chipMinimizers?.removed ?? [], 'chipMinimizer'],
    [summary.plumbing?.removed ?? [], 'plumbing'],
    [
      summary.selectedOptionalProducts?.removed ?? [],
      'selectedOptionalProducts',
    ],
  ];

  const removedCutouts: RemovedProduct[] =
    summary.cutouts?.removed.map((item) => ({
      id: String(item.cutout.productId),
      imageUrl: item.cutout.imageUrl,
      name: item.cutout.name,
      type: 'cutout',
    })) ?? [];

  const removedProducts: RemovedProduct[] = otherProducts.flatMap(
    ([items, type]) =>
      items.map((item) => ({
        id: String(item.selectedItem.productId),
        imageUrl: item.selectedItem.imageUrl || '',
        name: item.selectedItem.name,
        type,
      }))
  );

  const removedSideAppliances: RemovedProduct[] = (
    summary.sideAppliances?.removedItems ?? []
  ).map((item) => ({
    id: String(item.productId),
    imageUrl: item.beautyShotImageUrl || item.imageUrl || '',
    name: item.name,
    type: 'appliance',
  }));

  const allRemovedProducts = [
    ...removedProductsFromShape,
    ...removedSideAppliances,
    ...removedCutouts,
    ...removedProducts,
  ];

  const sanitizedRemovedProductsMap = new Map(
    allRemovedProducts.map((product) => [product.id, product])
  ).values();

  return Array.from(sanitizedRemovedProductsMap);
};
